import React, { useEffect, useState } from "react";

import { ChevronDown, ChevronRight, Plus, X } from "lucide-react";

import "./MailAccounts.css";

const COLORS = [
  "#2e8b3d",
  "#8b3fd9",
  "#d6306a",
  "#2f9d5a",
  "#e8590c",
  "#6a3fb5",
  "#1f9d8f",
  "#d6246e",
];

const colorFor = (text = "") =>
  COLORS[
    [...text].reduce((sum, char) => sum + char.charCodeAt(0), 0) % COLORS.length
  ];

const MIN_PASSWORD_LENGTH = 6;
const MAX_PASSWORD_LENGTH = 30;

const emptyForm = {
  email: "",
  password: "",
  label: "",
};

export default function MailAccounts({
  primaryEmail,
  accounts = [],
  active,
  busy,
  onSwitch,
  onAdd,
  onRemove,
}) {
  const [open, setOpen] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  const closeForm = () => {
    if (saving) return;

    setShowForm(false);
    setForm(emptyForm);
    setFormError("");
  };

  useEffect(() => {
    if (!showForm) return undefined;

    const onKey = (event) => {
      if (event.key === "Escape" && !saving) {
        setShowForm(false);
        setForm(emptyForm);
        setFormError("");
      }
    };

    window.addEventListener("keydown", onKey);

    return () => window.removeEventListener("keydown", onKey);
  }, [showForm, saving]);

  const setField = (key) => (event) => {
    setForm((value) => ({
      ...value,
      [key]: event.target.value,
    }));

    setFormError("");
  };

  const submit = async (event) => {
    event.preventDefault();

    if (saving || busy) return;

    const email = form.email.trim().toLowerCase();
    const password = form.password;
    const label = form.label.trim();

    if (!email) {
      setFormError("Please enter an email address.");
      return;
    }

    if (
      password.length < MIN_PASSWORD_LENGTH ||
      password.length > MAX_PASSWORD_LENGTH
    ) {
      setFormError("Mailbox password must be 6–30 characters.");
      return;
    }

    if (typeof onAdd !== "function") {
      setFormError("Mail account connection is not configured.");
      return;
    }

    setSaving(true);
    setFormError("");

    try {
      const account = await onAdd({
        email,
        password,
        label,
      });

      if (!account?.email) {
        throw new Error("Mail account connection was not confirmed.");
      }

      setShowForm(false);
      setForm(emptyForm);

      if (typeof onSwitch === "function") {
        await onSwitch(account.email);
      }
    } catch (err) {
      setFormError(err?.message || "Unable to add mail account.");
    } finally {
      setSaving(false);
    }
  };

  const rows = [
    {
      id: "main",
      email: primaryEmail,
      value: "",
      label: "Main mailbox",
    },
    ...accounts.map((account) => ({
      id: account.id,
      email: account.email,
      value: account.email,
      label: account.label,
      account,
    })),
  ];

  return (
    <div className="erp-acc">
      <div className="erp-acc-head">
        <button
          type="button"
          className="erp-acc-toggle"
          onClick={() => setOpen((value) => !value)}
        >
          {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}

          <strong>Accounts</strong>
        </button>

        <button
          type="button"
          className="erp-acc-add"
          title="Add mail account"
          onClick={() => {
            setForm(emptyForm);
            setFormError("");
            setShowForm(true);
          }}
          disabled={busy || saving}
        >
          <Plus size={17} />
        </button>
      </div>

      {open && (
        <div className="erp-acc-list">
          {rows.map((row) => {
            const isActive = (active || "") === row.value;

            return (
              <div
                key={row.id}
                className={`erp-acc-row ${isActive ? "active" : ""}`}
              >
                <button
                  type="button"
                  className="erp-acc-pick"
                  disabled={busy || saving}
                  title={row.email}
                  onClick={() => {
                    if (typeof onSwitch === "function") {
                      onSwitch(row.value);
                    }
                  }}
                >
                  <span
                    className="erp-acc-avatar"
                    style={{
                      backgroundColor: colorFor(row.email),
                    }}
                  >
                    {(row.email || "?")[0].toUpperCase()}
                  </span>

                  <span className="erp-acc-text">
                    <span className="erp-acc-email">
                      {row.email || "Loading..."}
                    </span>

                    {row.label && (
                      <span className="erp-acc-sub">{row.label}</span>
                    )}
                  </span>
                </button>

                {row.account && (
                  <button
                    type="button"
                    className="erp-acc-remove"
                    title="Remove this account"
                    disabled={busy || saving}
                    onClick={() => {
                      if (
                        window.confirm(
                          `Remove ${row.email} from your ERP email? The mailbox itself is not deleted.`,
                        )
                      ) {
                        if (typeof onRemove === "function") {
                          onRemove(row.account);
                        }
                      }
                    }}
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showForm && (
        <div
          className="erp-acc-overlay"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              closeForm();
            }
          }}
        >
          <form className="erp-acc-modal" onSubmit={submit} autoComplete="off">
            <div className="erp-acc-modal-head">
              <strong>Add mail account</strong>

              <button
                type="button"
                className="erp-acc-close"
                title="Close"
                onClick={closeForm}
                disabled={saving}
              >
                <X size={18} />
              </button>
            </div>

            <div className="erp-acc-modal-body">
              <label htmlFor="erp-acc-email">Email address</label>

              <input
                id="erp-acc-email"
                type="email"
                required
                autoFocus
                value={form.email}
                placeholder="name@softvolute.in"
                onChange={setField("email")}
                disabled={saving}
              />

              <label htmlFor="erp-acc-password">Mailbox password</label>

              <input
                id="erp-acc-password"
                type="password"
                required
                minLength={MIN_PASSWORD_LENGTH}
                maxLength={MAX_PASSWORD_LENGTH}
                autoComplete="current-password"
                value={form.password}
                placeholder="Enter mailbox password"
                onChange={setField("password")}
                disabled={saving}
              />

              <label htmlFor="erp-acc-label">Name (optional)</label>

              <input
                id="erp-acc-label"
                type="text"
                maxLength={60}
                value={form.label}
                placeholder="e.g. Accounts, Support"
                onChange={setField("label")}
                disabled={saving}
              />

              <div className="erp-acc-note">
                Enter the password of an existing MailEnable mailbox. The
                password will be verified by the server and stored encrypted if
                the connection succeeds.
              </div>

              {formError && (
                <div className="erp-acc-error" role="alert">
                  {formError}
                </div>
              )}
            </div>

            <div className="erp-acc-modal-foot">
              <button
                type="button"
                className="erp-acc-btn"
                disabled={saving}
                onClick={closeForm}
              >
                Cancel
              </button>

              <button
                type="submit"
                className="erp-acc-btn primary"
                disabled={
                  saving ||
                  busy ||
                  !form.email.trim() ||
                  form.password.length < MIN_PASSWORD_LENGTH ||
                  form.password.length > MAX_PASSWORD_LENGTH
                }
              >
                {saving ? "Checking..." : "Add account"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
